import { Button } from '@amore/ui/components/button'
import { Card, CardTitle } from '@amore/ui/components/card'
import { Text } from '@amore/ui/components/typography'
import { Check, Download, Link2, Printer } from 'lucide-react'
import type { ReactNode } from 'react'
import { formatDate } from '@/lib/format'
import { dayMinutes, photoOverview, photoRoleLabels } from '@/mock/photo'
import { useWedding } from '@/mock/store'
import type { WeddingData } from '@/mock/types'
import { callerLabel } from './groups'

function guestName(data: WeddingData, id: string | null) {
  const person = data.parties.flatMap((party) => party.persons).find((item) => item.id === id)
  return person ? `${person.firstName} ${person.lastName}`.trim() : null
}

/** The day as the photographer needs it: everything timed, minus other suppliers' arrivals. */
function briefProgram(data: WeddingData) {
  return data.schedule
    .filter(
      (event) =>
        !event.removed &&
        event.time &&
        (!event.title.startsWith('Příjezd:') || event.source?.section === 'photo'),
    )
    .sort((left, right) => dayMinutes(left.time ?? '') - dayMinutes(right.time ?? ''))
}

/** What the brief still lacks; each line says where to fill it in. */
function missing(data: WeddingData) {
  const { groups, wishes } = data.photo
  const crew = data.photo.crew.filter((member) => member.supplierId)
  return [
    !crew.some((member) => member.role === 'photo') && 'Fotograf (Přehled)',
    crew.some((member) => !member.arrival || !member.end) && 'Od kdy do kdy fotí (Přehled)',
    !groups.length && 'Skupinové fotky',
    groups.length > 0 && !data.photo.caller && 'Kdo svolává skupiny (Skupinové fotky)',
    !wishes && 'Přání pro fotografa (Přehled)',
    !data.schedule.some((event) => !event.removed && event.time) && 'Program dne (Harmonogram)',
  ].filter((item): item is string => Boolean(item))
}

export function PhotoBriefPage({
  onPreview,
}: {
  onPreview: (title: string, description?: string) => void
}) {
  const data = useWedding()
  const todo = missing(data)
  return (
    <div className="grid items-start gap-6 xl:grid-cols-[minmax(0,1fr)_20rem]">
      <BriefDocument data={data} />
      <div className="space-y-6 xl:sticky xl:top-6">
        <Card>
          <CardTitle as="h2">Pro fotografa</CardTitle>
          <Text tone="muted" className="mt-2">
            Jedna stránka se vším, co potřebuje vědět. Skládá se z toho, co máte vyplněné.
          </Text>
          <div className="mt-4 grid gap-2">
            <Button onClick={() => onPreview('Stahování PDF připravujeme')}>
              <Download className="size-4" aria-hidden="true" />
              Stáhnout PDF
            </Button>
            <Button
              appearance="outline"
              tone="neutral"
              onClick={() =>
                onPreview('Odkaz zkopírován', 'Fotograf v něm uvidí vždy aktuální verzi.')
              }
            >
              <Link2 className="size-4" aria-hidden="true" />
              Kopírovat odkaz
            </Button>
            <Button appearance="ghost" tone="neutral" onClick={() => window.print()}>
              <Printer className="size-4" aria-hidden="true" />
              Vytisknout
            </Button>
          </div>
          <Text variant="small" tone="muted" className="mt-3">
            Odkaz se vždy otevře v aktuální podobě. PDF je kopie z dnešního dne.
          </Text>
        </Card>
        <Card>
          <CardTitle as="h2">Ještě doplnit</CardTitle>
          {todo.length ? (
            <ul className="mt-3 space-y-2 text-sm">
              {todo.map((item) => (
                <li key={item} className="flex gap-2">
                  <span
                    aria-hidden="true"
                    className="mt-2 size-1.5 shrink-0 rounded-full bg-warning-indicator"
                  />
                  {item}
                </li>
              ))}
            </ul>
          ) : (
            <Text tone="muted" className="mt-3 inline-flex items-center gap-1.5">
              <Check className="size-4 text-success-foreground" aria-hidden="true" />
              Vše podstatné je vyplněné.
            </Text>
          )}
        </Card>
      </div>
    </div>
  )
}

function BriefDocument({ data }: { data: WeddingData }) {
  const { identity, ceremony } = data
  const crew = photoOverview(data).crew.filter((member) => member.supplier)
  const venue = data.suppliers.find((item) => item.id === ceremony.venueSupplierId)
  const address = data.catalog.find((item) => item.id === venue?.catalogServiceId)?.profile?.address
  const officiant = data.suppliers.find((item) => item.id === ceremony.officiantSupplierId)
  const caller = callerLabel(data)
  const program = briefProgram(data)
  const couple = [identity.brideName, identity.groomName].filter(Boolean).join(' a ')
  const people = [
    identity.brideName && { role: 'Nevěsta', name: identity.brideName },
    identity.groomName && { role: 'Ženich', name: identity.groomName },
    guestName(data, ceremony.brideWitnessId) && {
      role: 'Svědkyně nevěsty',
      name: guestName(data, ceremony.brideWitnessId),
    },
    guestName(data, ceremony.groomWitnessId) && {
      role: 'Svědek ženicha',
      name: guestName(data, ceremony.groomWitnessId),
    },
    officiant && { role: 'Oddávající', name: officiant.name },
    caller && { role: 'Svolává skupiny', name: caller.name },
  ].filter((item): item is { role: string; name: string } => Boolean(item))

  return (
    <article
      aria-label="Náhled podkladů pro fotografa"
      className="mx-auto w-full max-w-[52rem] rounded-panel border border-border bg-white px-6 py-8 shadow-sm sm:px-12 sm:py-12"
    >
      <header className="border-b border-border pb-6">
        <p className="text-xs font-semibold tracking-[0.14em] text-action uppercase">
          Podklady pro fotografa
        </p>
        <h2 className="mt-2 font-heading text-3xl tracking-tight">{couple || 'Vaše svatba'}</h2>
        <p className="mt-1 text-sm text-muted-foreground">
          {identity.date ? formatDate(identity.date, 'long') : 'Datum zatím nevybrané'}
          {venue ? ` · ${venue.name}${address ? `, ${address}` : ''}` : ''}
        </p>
      </header>

      <BriefSection title="Kdo fotí a natáčí">
        {crew.length ? (
          <ul className="space-y-1.5 text-sm">
            {crew.map((member) => (
              <li key={member.id} className="flex flex-wrap justify-between gap-x-4">
                <span>
                  <span className="font-semibold">
                    {member.supplier?.name ?? 'Zatím nevybraný'}
                  </span>
                  <span className="text-muted-foreground"> · {photoRoleLabels[member.role]}</span>
                </span>
                <span className="tabular-nums text-muted-foreground">
                  {member.arrival && member.end
                    ? `${member.arrival}–${member.end}`
                    : 'Čas upřesníme'}
                </span>
              </li>
            ))}
          </ul>
        ) : (
          <Empty />
        )}
      </BriefSection>

      <BriefSection title="Program dne">
        {program.length ? (
          <ol className="divide-y divide-border text-sm">
            {program.map((event) => (
              <li key={event.id} className="grid grid-cols-[3.5rem_minmax(0,1fr)] gap-x-4 py-2">
                <span className="font-semibold tabular-nums">{event.time}</span>
                <span>
                  {event.title}
                  {event.location ? (
                    <span className="text-muted-foreground"> · {event.location}</span>
                  ) : null}
                </span>
              </li>
            ))}
          </ol>
        ) : (
          <Empty />
        )}
      </BriefSection>

      <BriefSection title="Kdo je kdo">
        {people.length ? (
          <dl className="grid gap-x-6 gap-y-1.5 text-sm sm:grid-cols-2">
            {people.map((person) => (
              <div key={person.role} className="flex justify-between gap-3">
                <dt className="text-muted-foreground">{person.role}</dt>
                <dd className="font-semibold">{person.name}</dd>
              </div>
            ))}
          </dl>
        ) : (
          <Empty />
        )}
      </BriefSection>

      <BriefSection title="Skupinové fotky">
        {data.photo.groups.length ? (
          <ol className="space-y-1.5 text-sm">
            {data.photo.groups.map((group, index) => (
              <li key={group.id} className="flex items-start gap-3">
                <span
                  aria-hidden="true"
                  className="mt-0.5 size-4 shrink-0 rounded-sm border border-foreground/40"
                />
                <span className="min-w-0 flex-1">
                  <span className="tabular-nums text-muted-foreground">{index + 1}. </span>
                  {group.name}
                </span>
                {group.priority === 'if-time' ? (
                  <span className="shrink-0 text-xs text-muted-foreground">když bude čas</span>
                ) : null}
              </li>
            ))}
          </ol>
        ) : (
          <Empty />
        )}
      </BriefSection>

      <BriefSection title="Přání">
        {data.photo.wishes ? (
          <p className="text-sm whitespace-pre-line">{data.photo.wishes}</p>
        ) : (
          <Empty />
        )}
      </BriefSection>
    </article>
  )
}

function BriefSection({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="border-b border-border py-6 last:border-b-0 last:pb-0">
      <h3 className="mb-3 text-xs font-semibold tracking-[0.12em] text-muted-foreground uppercase">
        {title}
      </h3>
      {children}
    </section>
  )
}

function Empty() {
  return (
    <p className="text-sm text-muted-foreground italic">
      Zatím nevyplněno, v podkladech se nezobrazí.
    </p>
  )
}
